import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./Watchlist.module.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MdArrowForward } from "react-icons/md";
import WebService from "../../Services/WebService";
import { orderedProductImages, primaryProductImage } from "../../Services/productImages";
import { canAddToCart, stockStatusLabel } from "../../Services/stockAvailability";

interface BaseDesign {
  id: number | string;
  name: string;
  sku_id?: string;
  images?: { file_name: string }[];
  quantity?: number;
  measuring_unit?: string;
  in_stock?: boolean;
  can_add_to_cart?: boolean;
  stock_status?: string;
  stock_status_label?: string;
}
interface RawMaterial extends BaseDesign {
  category?: string;
  measuring_unit_value?: number;
  selling_price?: number;
  distributor_selling_price?: number;
  retailer_selling_price?: number;
  gst_percentage?: number;
  description?: string;
  status?: string;
}
interface ProductDesign extends BaseDesign {
  is_on_sale?: boolean;
  is_most_selling?: boolean;
  customer_sale_discount_per?: number;
  basic_door_price?: number;
  design_description?: string; 
}
type WatchlistItem = ProductDesign | RawMaterial;
interface PaginationData {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}

const Watchlist: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const customer_id = (location.state as any)?.customer_id || null;

  const [items, setItems] = useState<WatchlistItem[]>([]);
  const [, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const searchRef = useRef<NodeJS.Timeout | null>(null);
  const loaderRef = useRef<HTMLDivElement | null>(null);
   const [, setCategoryName] = useState<string>(
  ((location.state as any)?.category_name as string) || ""
);

  const getDescription = (item: WatchlistItem) =>
    (typeof (item as ProductDesign).design_description === "string" && (item as ProductDesign).design_description) ||
    (item as RawMaterial).description ||
    "";

  // Fetch Data
const fetchItems = useCallback(async (pageNum: number, searchValue = "") => {
  if (!type || !id) return;
  setLoading(true);
  setError("");
  try {
    let action = `/v1/door-management/dashboard/type/${type}/subcategories/${id}/watchlist?page_number=${pageNum}&limit=10`;
    if (searchValue) action += `&product_like=${encodeURIComponent(searchValue)}`;
    const res = await WebService.getAPI<any>({ action });
    const data = res.data;

    let newItems: WatchlistItem[] = [];
    if (type?.toUpperCase() === "PRODUCT") {
      const list = Array.isArray(data?.designs)
        ? data.designs
        : Array.isArray(data?.products)
          ? data.products
          : [];
      newItems = list.map((p: any) => ({
        ...p,
        id: p.id ?? p.product_id,
        name: p.name || p.product_name || "",
        images: orderedProductImages(p),
        design_description: p.design_description || p.description || "",
      }));
    } else if (type?.toUpperCase() === "RAW_MATERIAL") {
      const list = Array.isArray(data?.raw_materials)
        ? data.raw_materials
        : Array.isArray(data?.products)
          ? data.products
          : [];
      newItems = list.map((p: any) => ({
        ...p,
        id: p.id ?? p.product_id,
        name: p.name || p.product_name || "",
        images: orderedProductImages(p),
      }));
    }

    // infer category name if we don't already have one
    setCategoryName(prev =>
      prev ||
      data?.subcategory_name ||
      data?.sub_category_name ||
      data?.category_name ||
      // fallback: for raw materials the item often carries its category
      (type === "RAW_MATERIAL" && newItems.length > 0
        ? ((newItems[0] as RawMaterial)?.category || "")
        : "")
    );

    setItems(prev => (pageNum === 1 ? newItems : [...prev, ...newItems]));
    setPagination(data.pagination || null);
    if (data.pagination && data.pagination.current_page < data.pagination.total_pages) {
      setHasMore(true);
    } else {
      setHasMore(false);
    }
  } catch (e) {
    setError("Could not load watchlist. Please try again.");
    setHasMore(false);
  } finally {
    setLoading(false);
  }
}, [type, id]);

useEffect(() => {
  setItems([]);
  setPage(1);
  setHasMore(true);
  setSearch("");

  const fromState =
    (location.state as any)?.category_name ||
    (location.state as any)?.categoryName ||
    "";
  setCategoryName(fromState);

  fetchItems(1, "");
  // eslint-disable-next-line
}, [type, id]);


  // Infinite scroll observer
  useEffect(() => {
    if (loading || !hasMore) return;

    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && !loading && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    };
    const option = { threshold: 0.1 };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
    // eslint-disable-next-line
  }, [loading, hasMore]);

  // Fetch new page when page state increments
  useEffect(() => {
    if (page === 1) return; // already loaded on initial
    fetchItems(page, search);
    // eslint-disable-next-line
  }, [page]);

  // Debounced search handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {
      setItems([]);
      setPage(1);
      setHasMore(true);
      fetchItems(1, e.target.value);
    }, 350);
  };

  const handleExploreClick = () => {
    navigate("/shop");
  };

  const renderSkeletonCards = () =>
    Array.from({ length: 8 }).map((_, idx) => (
      <div key={idx} className={styles.card}>
        <div className={styles.skeletonImg} />
        <div className={styles.cardBody}>
          <div className={styles.skeletonText} style={{ width: "55%" }} />
          <div className={styles.skeletonText} style={{ width: "30%" }} />
          <div className={styles.skeletonPrice} style={{ width: "45%" }} />
          <div className={styles.skeletonText} style={{ width: "40%", marginTop: 8 }} />
        </div>
      </div>
    ));

  return (
    <div className={styles.watchlistPage}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>Your <span className={styles.highlight}>Watchlist</span></h2>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search by name..."
          value={search}
          onChange={handleSearchChange}
          // style={{ marginLeft: 16, minWidth: 240 }}
        />
        <div className={styles.exploreArrowWrapper} onClick={handleExploreClick}>
          <MdArrowForward className={styles.exploreArrow} />
        </div>
      </div>

      {error ? (
        <div className={styles.error}>{error}</div>
      ) : items.length === 0 && loading ? (
        <div className={styles.grid}>{renderSkeletonCards()}</div>
      ) : items.length === 0 ? (
        <div className={styles.error}>No products found in this category.</div>
      ) : (
        <div className={styles.grid}>
          {items.map((item) => (
            <div
              key={item.id}
              className={styles.card}
              style={{ cursor: "pointer" }}
              onClick={() =>
                navigate(`/watchlist/${type}/${id}/${item.id}`, {
                  state: {
                    item: {
                      ...item,
                      description: getDescription(item)
                    },
                    ...(customer_id && { customer_id })
                  }
                })
              }
            >
              <img
                src={primaryProductImage(item)}
                alt={item.name}
                className={styles.productImg}
                loading="lazy"
                onError={e => {
                  (e.target as HTMLImageElement).src = "/Assets/no-pictures.png";
                }}
              />
              {!canAddToCart(item) && stockStatusLabel(item) && (
                <span className={styles.stockBadge}>{stockStatusLabel(item)}</span>
              )}
              {"is_most_selling" in item && item.is_most_selling && (
                <span className={styles.mostSellingRibbon}>Trending</span>
              )}
              <div className={styles.cardBody}>
                <h3 className={styles.productName}>{item.name}</h3>
                <div className={styles.productDesc}>
                  {getDescription(item) || ""}
                </div>
                {item.sku_id && (
                  <span className={styles.skuId}>
                    SKU: {item.sku_id}
                  </span>
                )}
              </div>
            </div>
          ))}
          {hasMore && (
            <div ref={loaderRef} className={styles.infiniteLoader}>
              {loading && (
                <div className={styles.loadingSpinner}>
                  Loading more...
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Watchlist;
